import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Crown, CreditCard, Calendar, CheckCircle, Star, Zap } from "lucide-react";

import apiClient from "@/api/ApiClient";
import { useToast } from "@/hooks/use-toast";

interface SubscriptionSettingsProps {
  onBack: () => void;
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onNavigateToExplore: () => void;
  onCreateNew: () => void;
  onCancelSubscription: () => void;
  user?: {
    email?: string;
    name?: string;
    isPremium?: boolean;
    subscriptionTier?: string;
    subscriptionEnd?: string;
  };
}

export const SubscriptionSettings = ({ 
  onBack, 
  onNavigateToChat, 
  onNavigateToMyBoyfriends, 
  onNavigateToProfile, 
  onNavigateToSettings,
  onNavigateToExplore,
  onCreateNew,
  onCancelSubscription,
  user
}: SubscriptionSettingsProps) => {
  const { toast } = useToast();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleManageSubscription = async () => {
    try {
      const returnUrl = window.location.href;
      const data = await apiClient.createCustomerPortal('web', returnUrl);
      
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.message || 'Failed to create customer portal');
      }
    } catch (error: any) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to open billing portal. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUpgrade = async (plan: 'monthly' | 'yearly') => {
    try {
      const successUrl = window.location.origin + "/payment/success?session_id={CHECKOUT_SESSION_ID}";
      const cancelUrl = window.location.href;
      const data = await apiClient.createSubscriptionCheckout(plan, 'web', successUrl, cancelUrl);
      
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.message || 'Failed to create checkout');
      }
    } catch (error: any) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to start checkout process. Please try again.",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const premiumFeatures = [
    "Unlimited conversations with all AI boyfriends",
    "Priority response times (faster AI)",
    "Advanced personality customization",
    "Voice messages and audio responses",
    "Exclusive premium characters",
    "High-quality image generation",
    "No ads or interruptions",
    "Priority customer support"
  ];

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Main Content */}
      <div className="p-4 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-primary/20">
            <Button variant="ghost" size="icon" onClick={onNavigateToProfile}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              Subscription Management
            </h1>
          </div>

          {user?.isPremium ? (
            <>
              {/* Current Subscription */}
              <Card className="p-6 mb-6 border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5">
                <div className="flex items-center gap-3 mb-4">
                  <Crown className="h-6 w-6 text-primary" />
                  <h3 className="text-lg font-semibold">Current Subscription</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                      <span className="font-medium">Plan</span>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-primary font-semibold">
                          {user.subscriptionTier || "Premium"}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                      <span className="font-medium">Status</span>
                      <span className="text-green-600 font-medium">Active</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                      <span className="font-medium">Billing Email</span>
                      <span className="text-muted-foreground text-sm">{user.email}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                      <span className="font-medium">Next Billing</span>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground text-sm">
                          {formatDate(user.subscriptionEnd)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                      <span className="font-medium">Payment Method</span>
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground text-sm">•••• 4242</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                      <span className="font-medium">Monthly Cost</span>
                      <span className="font-semibold">$14.99</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  <Button variant="romantic" onClick={handleManageSubscription}>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Manage Billing
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => handleUpgrade('monthly')}>
                      <Star className="h-4 w-4 mr-2" />
                      Monthly
                    </Button>
                    <Button variant="outline" onClick={() => handleUpgrade('yearly')}>
                      <Star className="h-4 w-4 mr-2" />
                      Yearly
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Premium Features */}
              <Card className="p-6 mb-6 border-primary/20">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Your Premium Benefits</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {premiumFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 p-2">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          ) : (
            <>
              {/* Upgrade to Premium */}
              <Card className="p-6 mb-6 border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5">
                <div className="text-center mb-6">
                  <Crown className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-2">Upgrade to Premium</h3>
                  <p className="text-muted-foreground">
                    Unlock the full potential of your AI relationship experience
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Free Plan */}
                  <Card className="p-4 border-muted">
                    <h4 className="font-semibold mb-2">Free Plan</h4>
                    <p className="text-2xl font-bold mb-4">$0<span className="text-sm font-normal">/month</span></p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Limited conversations per day
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Basic personality options
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Standard response times
                      </li>
                    </ul>
                  </Card>

                  {/* Premium Plan */}
                  <Card className="p-4 border-primary bg-primary/5 relative">
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary text-white px-3 py-1 rounded-full text-xs font-medium">
                        Most Popular
                      </span>
                    </div>
                    <h4 className="font-semibold mb-2 text-primary">Premium Plan</h4>
                    <p className="text-2xl font-bold mb-4">$14.99<span className="text-sm font-normal">/month</span></p>
                    <ul className="space-y-2 text-sm mb-4">
                      {premiumFeatures.slice(0, 4).map((feature, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          {feature}
                        </li>
                      ))}
                      <li className="text-xs text-muted-foreground">+ 4 more features</li>
                    </ul>
                    <div className="space-y-2">
                      <Button className="w-full" variant="romantic" onClick={() => handleUpgrade('monthly')}>
                        <Crown className="h-4 w-4 mr-2" />
                        Upgrade to Monthly Plan
                      </Button>
                      <Button className="w-full" variant="outline" onClick={() => handleUpgrade('yearly')}>
                        <Crown className="h-4 w-4 mr-2" />
                        Upgrade to Yearly Plan (Save 50%)
                      </Button>
                    </div>
                  </Card>
                </div>
              </Card>

              {/* All Premium Features */}
              <Card className="p-6 border-primary/20">
                <h3 className="font-semibold mb-4">Everything Included in Premium</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {premiumFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 p-2">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}

          {/* Cancel Subscription - Danger Zone (only for premium users) */}
          {user?.isPremium && (
            <Card className="p-6 mt-6 border-red-200 border-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-red-600 mb-1">Cancel Subscription</h3>
                  <p className="text-sm text-muted-foreground">
                    Cancel your premium subscription. You'll keep access until the end of your billing period.
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setShowCancelConfirm(true)}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Cancel Subscription
                </Button>
              </div>
            </Card>
          )}

          {/* Cancel Confirmation Modal */}
          {showCancelConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCancelConfirm(false)}>
              <Card className="p-6 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-semibold mb-2">Cancel Subscription?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Are you sure you want to cancel your premium subscription? You'll lose access to all premium features at the end of your billing period.
                </p>
                <div className="flex gap-3">
                  <Button 
                    variant="romantic" 
                    className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    onClick={() => setShowCancelConfirm(false)}
                  >
                    Keep Premium
                  </Button>
                  <Button variant="ghost" className="text-muted-foreground hover:text-foreground" onClick={() => {
                    onCancelSubscription();
                    setShowCancelConfirm(false);
                    onNavigateToExplore();
                  }}>
                    Cancel Subscription
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};